import { clients, HttpError } from 'lib/https';
import { getConfigurationSettings } from 'services/configuration-service';
import type {
  GetScheduledJobsPayload,
  GetScheduledJobsResponse,
  ScheduledJobItem,
  ScheduledJobRunItem,
  ScheduledJobRunListResponse,
  ScheduledJobStatus,
  ScheduledJobStatusResponse,
  SchedulePreviewResponse,
  ScheduleSpecDto,
  UpdateScheduledJobPayload,
} from './scheduled-jobs.types';

const SCHEDULED_JOB_STATUSES: ScheduledJobStatus[] = [
  'active',
  'paused',
  'completed',
  'expired',
  'failed',
];

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const normalizeScheduleSpec = (raw: unknown): ScheduleSpecDto | null => {
  const record = asRecord(raw);
  if (!record) return null;
  const scheduleKind =
    asString(record.scheduleKind) || asString(record.schedule_kind) || 'cron';
  const timezone = asString(record.timezone) || asString(record.Timezone) || 'UTC';
  return {
    scheduleKind: scheduleKind === 'once' ? 'once' : 'cron',
    cron: asString(record.cron) || asString(record.Cron),
    runAt: asString(record.runAt) || asString(record.run_at) || asString(record.RunAt),
    timezone,
    startAt: asString(record.startAt) || asString(record.start_at) || asString(record.StartAt),
    endAt: asString(record.endAt) || asString(record.end_at) || asString(record.EndAt),
    maxRuns:
      typeof record.maxRuns === 'number'
        ? record.maxRuns
        : typeof record.max_runs === 'number'
          ? record.max_runs
          : null,
  };
};

const normalizeStatus = (value: unknown): ScheduledJobStatus | string => {
  if (typeof value !== 'string') return 'active';
  const normalized = value.trim().toLowerCase();
  return SCHEDULED_JOB_STATUSES.includes(normalized as ScheduledJobStatus)
    ? (normalized as ScheduledJobStatus)
    : normalized || 'active';
};

const normalizeScheduledJobItem = (raw: unknown): ScheduledJobItem | null => {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const id =
    asString(record.id) ||
    asString(record._id) ||
    asString(record.Id) ||
    asString(record.jobId) ||
    asString(record.job_id);

  if (!id) return null;

  const name =
    asString(record.name) ||
    asString(record.Name) ||
    asString(record.title) ||
    asString(record.Title) ||
    'Untitled';

  const prompt =
    asString(record.prompt) ||
    asString(record.Prompt) ||
    null;
  const description =
    asString(record.description) ||
    asString(record.Description) ||
    null;

  return {
    id,
    name,
    description,
    prompt: prompt || description,
    status: normalizeStatus(record.status ?? record.Status),
    agentId: asString(record.agentId) || asString(record.agent_id) || asString(record.AgentId),
    agentName:
      asString(record.agentName) || asString(record.agent_name) || asString(record.AgentName),
    conversationId:
      asString(record.conversationId) ||
      asString(record.conversation_id) ||
      asString(record.ConversationId),
    nextRunAt:
      asString(record.nextRunAt) ||
      asString(record.next_run_at) ||
      asString(record.NextRunAt),
    nextRunAtLocal:
      asString(record.nextRunAtLocal) ||
      asString(record.next_run_at_local) ||
      asString(record.NextRunAtLocal),
    lastRunAt:
      asString(record.lastRunAt) ||
      asString(record.last_run_at) ||
      asString(record.LastRunAt),
    createdDate:
      asString(record.createdDate) ||
      asString(record.created_date) ||
      asString(record.CreatedDate),
    hilEnabled:
      (record.hilEnabled ?? record.hil_enabled ?? record.HilEnabled) === true,
    getMail:
      (record.getMail ?? record.get_mail ?? record.GetMail) !== false,
    pushNotification:
      (record.pushNotification ?? record.push_notification ?? record.PushNotification) !== false,
    schedule: normalizeScheduleSpec(record.schedule ?? record.Schedule),
    running: (record.running ?? record.Running) === true,
  };
};

const normalizeRunItem = (raw: unknown): ScheduledJobRunItem | null => {
  const record = asRecord(raw);
  if (!record) return null;
  const id = asString(record.id) || asString(record.Id);
  if (!id) return null;
  return {
    id,
    status: asString(record.status) || asString(record.Status) || 'unknown',
    trigger: asString(record.trigger) || asString(record.Trigger) || '',
    scheduledFor: asString(record.scheduledFor) || asString(record.scheduled_for),
    startedAt: asString(record.startedAt) || asString(record.started_at),
    finishedAt: asString(record.finishedAt) || asString(record.finished_at),
    errorMessage: asString(record.errorMessage) || asString(record.error_message),
  };
};

const normalizeScheduledJobsResponse = (raw: unknown): GetScheduledJobsResponse => {
  if (!raw || typeof raw !== 'object') {
    return { items: [], total: 0, skip: 0, take: 25 };
  }

  const record = raw as Record<string, unknown>;
  const list = Array.isArray(record.items)
    ? record.items
    : Array.isArray(record.data)
      ? record.data
      : Array.isArray(raw)
        ? raw
        : [];

  const items = list
    .map(normalizeScheduledJobItem)
    .filter((item): item is ScheduledJobItem => item !== null);

  return {
    items,
    total: typeof record.total === 'number' ? record.total : items.length,
    skip: typeof record.skip === 'number' ? record.skip : 0,
    take: typeof record.take === 'number' ? record.take : items.length || 25,
  };
};

class ScheduledJobsService {
  async getScheduledJobs(
    payload: GetScheduledJobsPayload,
    blocksKey: string
  ): Promise<GetScheduledJobsResponse> {
    const config = await getConfigurationSettings();
    const params = new URLSearchParams();

    if (payload.agentId?.trim()) params.set('agentId', payload.agentId.trim());
    const statuses = Array.isArray(payload.status) ? payload.status : [payload.status];
    const status = statuses.filter(Boolean).join(',');
    if (status) params.set('status', status);
    if (payload.conversationId?.trim()) {
      params.set('conversationId', payload.conversationId.trim());
    }
    if (payload.q?.trim()) params.set('q', payload.q.trim());
    params.set('skip', String(payload.skip ?? 0));
    params.set('take', String(Math.min(payload.take ?? 25, 200)));
    params.set('sort', payload.sort ?? 'nextRunAt');
    params.set('order', payload.order ?? 'asc');

    const query = params.toString();
    const response = await clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.scheduledJobs}?${query}`,
      blocksKey
    );

    return normalizeScheduledJobsResponse(response);
  }

  /**
   * One routine by id, or null when the API hides it (404 for a deleted, unknown or
   * someone else's routine; 403 is treated the same so the link reveals nothing).
   */
  async getScheduledJob(
    jobId: string,
    blocksKey: string,
    signal?: AbortSignal
  ): Promise<ScheduledJobItem | null> {
    const config = await getConfigurationSettings();
    try {
      const response = await clients.request<unknown>(
        `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.scheduledJobs}/${encodeURIComponent(jobId)}`,
        blocksKey,
        { method: 'GET', signal }
      );
      return normalizeScheduledJobItem(response);
    } catch (error) {
      if (error instanceof HttpError && (error.status === 404 || error.status === 403)) {
        return null;
      }
      throw error;
    }
  }

  /** Soft delete — run history is preserved (`deleteConversation=false`). */
  async deleteScheduledJob(
    jobId: string,
    blocksKey: string,
    options?: { deleteConversation?: boolean }
  ): Promise<void> {
    const config = await getConfigurationSettings();
    const params = new URLSearchParams();
    params.set('deleteConversation', String(options?.deleteConversation ?? false));

    await clients.delete(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.scheduledJobs}/${encodeURIComponent(jobId)}?${params.toString()}`,
      blocksKey
    );
  }

  /** Partial update — send only fields that changed. Resolves to the saved job, or null if the body is not a job. */
  async updateScheduledJob(
    jobId: string,
    payload: UpdateScheduledJobPayload,
    blocksKey: string
  ): Promise<ScheduledJobItem | null> {
    const config = await getConfigurationSettings();
    const response = await clients.request<unknown>(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.scheduledJobs}/${encodeURIComponent(jobId)}`,
      blocksKey,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }
    );
    return normalizeScheduledJobItem(response);
  }

  async pauseScheduledJob(jobId: string, blocksKey: string): Promise<ScheduledJobStatusResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.scheduledJobs}/${encodeURIComponent(jobId)}/pause`,
      blocksKey,
      ''
    );
  }

  async resumeScheduledJob(jobId: string, blocksKey: string): Promise<ScheduledJobStatusResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.scheduledJobs}/${encodeURIComponent(jobId)}/resume`,
      blocksKey,
      ''
    );
  }

  async runScheduledJobNow(jobId: string, blocksKey: string): Promise<void> {
    const config = await getConfigurationSettings();
    await clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.scheduledJobs}/${encodeURIComponent(jobId)}/run-now`,
      blocksKey,
      ''
    );
  }

  /** Stateless — no job needed. Used for live preview while the user edits a schedule. */
  async previewSchedule(
    spec: ScheduleSpecDto & { count?: number },
    blocksKey: string
  ): Promise<SchedulePreviewResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.scheduledJobs}/preview`,
      blocksKey,
      JSON.stringify(spec)
    );
  }

  async getScheduledJobRuns(
    jobId: string,
    blocksKey: string,
    take = 5
  ): Promise<ScheduledJobRunListResponse> {
    const config = await getConfigurationSettings();
    const raw = await clients.get<{ items?: unknown[]; total?: number; skip?: number; take?: number }>(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.scheduledJobs}/${encodeURIComponent(jobId)}/runs?take=${take}`,
      blocksKey
    );
    const items = (raw.items ?? [])
      .map(normalizeRunItem)
      .filter((item): item is ScheduledJobRunItem => item !== null);
    return { items, total: raw.total ?? items.length, skip: raw.skip ?? 0, take: raw.take ?? take };
  }
}

export const scheduledJobsService = new ScheduledJobsService();
